import { AssistantPanel } from '@/components/assistant/AssistantPanel';
import { BbSubtitle } from '@/components/bb/BbSubtitle';
import { CardFace } from '@/components/cards/CardFace';
import { CharacterAvatar } from '@/components/character/CharacterAvatar';
import type { Card } from '@/engine/types';

export type HeroPanelProps = {
  stack?: number;
  holeCards?: readonly [Card, Card];
  bbSize?: number;
  /** Hero's turn-active / winner state — drives the avatar ring + animation. */
  isTurn?: boolean;
  isWinner?: boolean;
};

/**
 * Below-the-table row hosting the hero's on-screen presence. Layout
 * (left → right):
 *
 *   [ AI helper (speech bubble floats up out of panel) ]
 *   [ Hero character avatar ]
 *   [ 2 hero hole cards — visually centered ]
 *   [ YOU stack + BB readout ]
 *
 * The hero AVATAR used to live at the bottom of the Table felt; it was
 * moved here to claim the Table for opponents + community + pot so
 * thinking bubbles and 5 community cards all fit on short phones.
 */
export function HeroPanel({ stack, holeCards, bbSize = 0, isTurn, isWinner }: HeroPanelProps) {
  return (
    <section
      className="relative flex items-center justify-between gap-2 px-3 py-2 bg-bg-elev/85 border-t-2 border-border"
      data-testid="hero-panel"
    >
      <div className="shrink-0">
        <AssistantPanel />
      </div>
      <div className="shrink-0">
        <CharacterAvatar codename="HERO" size="sm" active={isTurn && !isWinner} winner={isWinner} />
      </div>
      <div className="flex gap-1 shrink-0" data-testid="hero-hole-cards">
        <CardFace card={holeCards?.[0]} size="lg" />
        <CardFace card={holeCards?.[1]} size="lg" />
      </div>
      <div className="text-right flex flex-col items-end gap-0 leading-tight min-w-[56px]">
        <span className="font-display text-[10px] text-muted uppercase tracking-widest">YOU</span>
        <span className="num text-lg text-fg leading-none">{stack ?? 0}</span>
        <BbSubtitle chips={stack ?? 0} bbSize={bbSize} tone="warning" className="text-[10px]" />
      </div>
    </section>
  );
}
